import React, { useState, useEffect, useCallback } from "react";
import {
  DataGrid,
  GridColDef,
  GridSortModel,
  GridPaginationModel,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TextField,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { format } from "date-fns";

import { UserForTable } from "../../types/user";
import {
  fetchUsersForMainWallet,
  PaginatedUsersWithBalancesResponseDto,
  UserWithBalancesResponseDto,
} from "service/api";
import { useLazyBalancesForMainUserClientQuery } from "features/finance/financeApi";

const UsersTable: React.FC = () => {
  const [rows, setRows] = useState<UserForTable[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "updatedAt", sort: "desc" },
  ]);

  const [loginFilter, setLoginFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [clientIdFilter, setClientIdFilter] = useState("");
  const [fullNameFilter, setFullNameFilter] = useState("");

  const [open, setOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [
    fetchBalances,
    {
      data: balancesData,
      isLoading: balancesLoading,
      isFetching: balancesFetching,
    },
  ] = useLazyBalancesForMainUserClientQuery();

  const loadPage = useCallback(async () => {
    setLoading(true);
    try {
      const { page, pageSize } = paginationModel;
      const sort = sortModel[0]?.field ?? "updatedAt";
      const direction =
        sortModel[0]?.sort?.toUpperCase() === "ASC" ? "ASC" : "DESC";

      const data: PaginatedUsersWithBalancesResponseDto =
        await fetchUsersForMainWallet(
          pageSize,
          page,
          sort,
          direction as "ASC" | "DESC",
          null,
          null,
          loginFilter,
          phoneFilter,
          clientIdFilter,
          fullNameFilter
        );

      setRows(
        data.usersWithBalances.map(
          (item: UserWithBalancesResponseDto) => item.user
        )
      );
      setRowCount(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [
    paginationModel,
    sortModel,
    loginFilter,
    phoneFilter,
    clientIdFilter,
    fullNameFilter,
  ]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const handleSearch = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    loadPage();
  };
  const handleReset = () => {
    setLoginFilter("");
    setPhoneFilter("");
    setClientIdFilter("");
    setFullNameFilter("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns: GridColDef<UserForTable>[] = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "email", headerName: "Email", width: 220 },
    { field: "firstName", headerName: "First Name", width: 140 },
    { field: "lastName", headerName: "Last Name", width: 140 },
    {
      field: "clientId",
      headerName: "Client ID",
      width: 120,
      valueGetter: (params) => params.row.clientId,
    },
    { field: "phone", headerName: "Phone", width: 140 },
    {
      field: "updatedAt",
      headerName: "Updated At",
      width: 160,
      valueFormatter: ({ value }) => {
        if (value == null) return "";
        const date = new Date(Number(value));
        return format(date, "dd/MM/yyyy");
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<UserForTable>) => (
        <Tooltip title="View balances">
          <IconButton
            size="small"
            onClick={() => {
              const client = params.row.clientId;
              setSelectedClient(client);
              fetchBalances(client);
              setOpen(true);
            }}
          >
            <InfoIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Box mb={2} display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <TextField
          label="Login"
          value={loginFilter}
          onChange={(e) => setLoginFilter(e.target.value)}
          size="small"
        />
        <TextField
          label="Phone"
          value={phoneFilter}
          onChange={(e) => setPhoneFilter(e.target.value)}
          size="small"
        />
        <TextField
          label="Client ID"
          value={clientIdFilter}
          onChange={(e) => setClientIdFilter(e.target.value)}
          size="small"
        />
        <TextField
          label="Full Name"
          value={fullNameFilter}
          onChange={(e) => setFullNameFilter(e.target.value)}
          size="small"
        />
        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>
        <Button variant="outlined" onClick={handleReset}>
          Reset
        </Button>
      </Box>

      <Box sx={{ height: 600, width: "100%" }}>
        <DataGrid<UserForTable>
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          sortingMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => setPaginationModel(model)}
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
          autoHeight
        />
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Balances for client {selectedClient}</DialogTitle>
        <DialogContent dividers>
          {(balancesLoading || balancesFetching) && (
            <Box textAlign="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {!(balancesLoading || balancesFetching) && balancesData && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Details
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Asset</TableCell>
                      <TableCell align="left">Address</TableCell>
                      <TableCell align="right">Available</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">EUR</TableCell>
                      <TableCell align="right">USD</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {balancesData.newArchBalances.details.map((b) => (
                      <TableRow key={b.asset}>
                        <TableCell>{b.asset.toUpperCase()}</TableCell>
                        <TableCell align="left">{b.address}</TableCell>
                        <TableCell align="right">{b.available}</TableCell>
                        <TableCell align="right">{b.total}</TableCell>
                        <TableCell align="right">
                          {b.availableEur.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          {b.availableUsd.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersTable;
